import {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { IMultiplyModel } from '@src/slide/domain/ui-model/element-modeles/Interface/multiply-model.interface';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { GraphicsType, ICustomPath } from '@tencent/alloy-graphics';
import {
    IShapeModelStyle,
} from '@src/slide/domain/ui-model/element-modeles/Interface/model-props/shape-model-props.interface';
import set from 'lodash/set';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import {
    IPictureModelStyle,
} from '@src/slide/domain/ui-model/element-modeles/Interface/model-props/picture-model-props.interface';
import { isSketchStyle } from '@src/slide/domain/ui-model/element-modeles/property/border/sketch/sketch-style';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { PathProcessor } from '@src/slide/domain/ui-model/element-modeles/property/border/sketch/path-processor';
import SketchUpdateAndRemove from '@src/slide/domain/ui-model/element-modeles/property/border/sketch/update-remove';

export class BorderSketchManager {
    private model: IShapeModel | IPictureModel;
    /**
     * 元素大小变更后更新 pathList
     * @param model
     * @param style
     */
    public updatePathListWhenSizeChange(
        model: IElementBaseModel,
        style?: Partial<IShapeModelStyle | IPictureModelStyle>,
    ): void {
        this.model = model as IShapeModel | IPictureModel;
        if (!isSketchStyle(this.borderInfo?.sketchStyle?.type)) {
            return;
        }

        const pathList = model.operatingGraphicInfo?.pathList
            ? model.operatingGraphicInfo.pathList
            : PathProcessor.generateSketchPathList({
                model: this.model,
                type: this.borderInfo?.sketchStyle?.type,
                style,
            });

        const props = {
            style: {
                bgShape: GraphicsType.CUSTOM,
                customGeom: SketchUpdateAndRemove.getCustomGeom(this.model, pathList),
            },
        };

        if (style?.controlValues) {
            set(props, 'style.border.sketchStyle.bgShape', this.borderInfo?.sketchStyle?.bgShape);
            set(props, 'style.border.sketchStyle.controlValues', style.controlValues);
            set(props, 'style.border.sketchStyle.type', this.borderInfo?.sketchStyle?.type);
        }

        if (style?.bgShape) {
            set(props, 'style.border.sketchStyle.bgShape', style.bgShape);
            set(props, 'style.border.sketchStyle.controlValues', []);
            set(props, 'style.border.sketchStyle.type', this.borderInfo?.sketchStyle?.type);
        }

        set(this.model, 'operatingGraphicInfo', null);
        this.model.updateProps(props);
    }

    public updatePathListUIWhenSizeChange(
        model: IElementBaseModel,
        style?: Partial<IShapeModelStyle | IPictureModelStyle>,
    ): void {
        this.model = model as IShapeModel | IPictureModel;
        if (!isSketchStyle(this.borderInfo?.sketchStyle?.type)) {
            return;
        }

        const notCalculatePathList = model.operatingGraphicInfo?.pathList?.[0]?.width === style?.width
            && model.operatingGraphicInfo?.pathList?.[0]?.height === style?.height;
        if (notCalculatePathList) return;

        const pathList = PathProcessor.generateSketchPathList({
            model: this.model,
            type: this.borderInfo?.sketchStyle?.type,
            style,
        });

        set(this.model, 'operatingGraphicInfo.pathList', pathList);
    }

    /**
     * 应用格式刷后更新 PathList
     * @param model 目标元素 A
     * @param style B 元素属性
     */
    public updatePathWithFormatBrush(
        model: IShapeModel | IPictureModel,
        style?: Partial<IShapeModelStyle | IPictureModelStyle>,
    ): void {
        if (model.type !== UIModelType.SHAPE && model.type !== UIModelType.IMAGE) {
            // 只有 shape 和 image 可以应用草绘样式
            return;
        }
        this.model = model;
        if (!isSketchStyle(this.borderInfo?.sketchStyle?.type) && !isSketchStyle(style?.border?.sketchStyle?.type)) {
            // A、B 元素均无草绘样式
            return;
        }
        if (this.borderInfo?.sketchStyle?.type === style?.border?.sketchStyle?.type) {
            // A、B 元素草绘格式相同
            return;
        }

        const update = {};
        const remove = {};
        SketchUpdateAndRemove.updateSketchUpdateAndRemoveProps(model, {
            style,
        }, {
            update,
            remove,
        });
        this.model.updateAndRemoveProps(update, remove);
    }

    public getSketchPathList(
        model: IShapeModel | IPictureModel,
        style?: Partial<IShapeModelStyle | IPictureModelStyle>,
    ): ICustomPath[] {
        this.model = model;
        return PathProcessor.generateSketchPathList({
            model: this.model,
            type: this.borderInfo?.sketchStyle?.type,
            style,
        });
    }

    private get borderInfo() {
        return (this.model as IMultiplyModel | IElementBaseModel).getBorder?.()
            .getBorderInfo();
    }
}
